import { useEffect, useRef } from 'react';

import { Box } from '@mui/material';

import type { TSxStyles } from '@/types/common';

import { ensureGoogleMaps } from '@/scripts/googleMaps';

type TMapProps = Pick<google.maps.MapOptions, 'mapTypeId' | 'disableDefaultUI'> &
  TMapEventHandlers & {
    lat: number;
    lng: number;
    zoom: number;
    fitBounds?: google.maps.LatLngBounds;
    /**
     * Extra map options, built once when the map is created (after Google Maps has loaded, so it
     * may read `google.maps`). They win over `disableDefaultUI` and `mapTypeId`. Optional and
     * additive: callers that omit it get the same map as before.
     */
    getOptions?: () => google.maps.MapOptions;
    sx?: TSxStyles;
  };

type TMapEventHandlers = {
  onReady?: (map: google.maps.Map) => void;
  onCenterChange?: (map: google.maps.Map) => void;
};

export const Map = ({
  lat,
  lng,
  zoom,
  mapTypeId,
  disableDefaultUI,
  sx,
  fitBounds,
  getOptions,
  onReady,
  onCenterChange,
}: TMapProps) => {
  const map = useRef<google.maps.Map>();
  const ref = useRef<HTMLDivElement>(null);
  const changesLocked = useRef(false);

  const handlers = useRef<TMapEventHandlers>({});

  handlers.current.onReady = onReady;
  handlers.current.onCenterChange = onCenterChange;

  useEffect(() => {
    let mounted = true;

    ensureGoogleMaps().then(() => {
      if (!mounted || !ref.current) {
        return;
      }

      const mapInstance = new google.maps.Map(ref.current, {
        disableDefaultUI,
        mapTypeId,
        zoom,
        center: {
          lat,
          lng,
        },
        ...getOptions?.(),
      });

      mapInstance.addListener('idle', () => {
        handlers.current.onReady?.(mapInstance);
        map.current = mapInstance;
      });

      mapInstance.addListener('center_changed', () => {
        if (!changesLocked.current && handlers.current.onCenterChange) {
          handlers.current.onCenterChange(mapInstance);
        }
      });
    });

    return () => {
      mounted = false;
    };

    // This one is just for initialising the map, ignore dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    changesLocked.current = false;
  });

  useEffect(() => {
    if (map.current && lat && lng) {
      changesLocked.current = true;
      map.current.setCenter({ lat, lng });
    }
  }, [lat, lng]);

  useEffect(() => {
    if (map.current && zoom) {
      changesLocked.current = true;
      map.current.setZoom(zoom);
    }
  }, [zoom]);

  useEffect(() => {
    if (map.current && fitBounds) {
      changesLocked.current = true;
      map.current.fitBounds(fitBounds);
    }
  }, [fitBounds]);

  return <Box ref={ref} sx={sx} />;
};
